/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import useSWR from 'swr';
import axios from 'axios';

import type * as Type from '@/common/interface';
import { LOGGED_TOKEN_STORAGE_KEY } from '@/common/constants';
import Storage from '@/utils/storage';

const terminalDevicesURL = '/answer/api/v1/terminal-control/devices';
const includeComputerNameQuery = 'include_computer_name=1';

const terminalControlGet = async <T>(url: string): Promise<T> => {
  const response = await axios.get<T>(url, {
    timeout: 10_000,
    withCredentials: true,
    headers: {
      Authorization: Storage.get(LOGGED_TOKEN_STORAGE_KEY) || '',
    },
  });
  return response.data;
};

const loadTerminalDeviceOverview =
  async (): Promise<Type.TerminalControlDeviceOverview> => {
    const result = await terminalControlGet<{
      devices: Type.TerminalControlDevice[];
    }>(
      `${terminalDevicesURL}?${includeComputerNameQuery}`,
    );
    const statuses = await Promise.all(
      (result.devices || []).map(async (device) => {
        try {
          return await terminalControlGet<Type.TerminalControlStatus>(
            `${terminalDevicesURL}/${encodeURIComponent(device.device_id)}/status?${includeComputerNameQuery}`,
          );
        } catch {
          return {
            device,
            realtime_status_unavailable: true,
          } satisfies Type.TerminalControlStatus;
        }
      }),
    );

    return {
      devices: statuses,
      total: statuses.length,
      online: statuses.filter((item) => item.realtime?.online).length,
    };
  };

export const useTerminalControlDevices = () => {
  const { data, error, isValidating, mutate } =
    useSWR<Type.TerminalControlDeviceOverview>(
      'terminal-control-device-overview',
      loadTerminalDeviceOverview,
      {
        refreshInterval: 15_000,
        revalidateOnFocus: true,
      },
    );

  return {
    data,
    error,
    isLoading: !data && !error,
    isValidating,
    mutate,
  };
};
